import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { STATIC } from '../../lib/queryPresets';
import {
  PARAPHEUR_CIRCUIT_QUERY,
  PARAPHEUR_LIST_QUERY,
} from '../../services/signature/parapheurFreshness';
import { useAuthStore } from '../../store/useAuthStore';
import { toSignInbox } from '../../services/signature/circuitRevision';
import {
  cancelCircuit,
  createCircuit,
  dispatchCircuit,
  getCircuit,
  getMyParapheUrl,
  getMySignatureUrl,
  listMyPendingSteps,
  listMyRejectedSteps,
  listMySentCircuits,
  listMySignedSteps,
  rejectStep,
  resubmitCircuit,
  setParapheStamps,
  setParapheZones,
  reauthAndSignStep,
  uploadMyParaphe,
  uploadMySignature,
  type CreateCircuitInput,
} from '../../services/signature/signatureService';

export const signatureKeys = {
  all: ['signature'] as const,
  pending: () => ['signature', 'pending'] as const,
  signed: () => ['signature', 'signed'] as const,
  rejected: () => ['signature', 'rejected'] as const,
  sent: () => ['signature', 'sent'] as const,
  circuit: (id: string) => ['signature', 'circuit', id] as const,
  mySignatureUrl: () => ['signature', 'mySignatureUrl'] as const,
  myParapheUrl: () => ['signature', 'myParapheUrl'] as const,
};

export function useMyPendingSteps() {
  const isInit = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);
  return useQuery({
    ...PARAPHEUR_LIST_QUERY,
    queryKey: signatureKeys.pending(),
    queryFn: listMyPendingSteps,
    enabled: isInit && !!session,
  });
}

export function useMySignedSteps() {
  const isInit = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);
  return useQuery({
    ...PARAPHEUR_LIST_QUERY,
    queryKey: signatureKeys.signed(),
    queryFn: listMySignedSteps,
    enabled: isInit && !!session,
  });
}

export function useMyRejectedSteps() {
  const isInit = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);
  return useQuery({
    ...PARAPHEUR_LIST_QUERY,
    queryKey: signatureKeys.rejected(),
    queryFn: listMyRejectedSteps,
    enabled: isInit && !!session,
  });
}

export function useMySentCircuits() {
  const isInit = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);
  return useQuery({
    ...PARAPHEUR_LIST_QUERY,
    queryKey: signatureKeys.sent(),
    queryFn: listMySentCircuits,
    enabled: isInit && !!session,
  });
}

export function useCircuit(id: string | undefined) {
  return useQuery({
    ...PARAPHEUR_CIRCUIT_QUERY,
    queryKey: signatureKeys.circuit(id ?? ''),
    queryFn: () => getCircuit(id!),
    enabled: !!id,
  });
}

export function useMySignatureUrl() {
  const collab = useAuthStore((s) => s.collaborator);
  return useQuery({
    ...STATIC,
    queryKey: signatureKeys.mySignatureUrl(),
    queryFn: getMySignatureUrl,
    enabled: !!collab?.signature_image_path,
  });
}

export function useMyParapheUrl() {
  const collab = useAuthStore((s) => s.collaborator) as
    | { paraphe_image_path?: string | null }
    | null;
  return useQuery({
    ...STATIC,
    queryKey: signatureKeys.myParapheUrl(),
    queryFn: getMyParapheUrl,
    enabled: !!collab?.paraphe_image_path,
  });
}

export function useTotalParapheurPending(): number {
  const { data } = useMyPendingSteps();
  return toSignInbox(data ?? []).length;
}

export function useCreateCircuit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCircuitInput) => createCircuit(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: signatureKeys.all });
      qc.invalidateQueries({ queryKey: ['dossier-files'] });
      qc.invalidateQueries({ queryKey: ['dossier-files-all'] });
    },
  });
}

export function useSignStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stepId, password }: { stepId: string; password: string }) =>
      reauthAndSignStep(stepId, password),
    onSuccess: () => qc.invalidateQueries({ queryKey: signatureKeys.all }),
  });
}

export function useSetParapheZones() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ circuitId, zones }: { circuitId: string; zones: unknown }) =>
      setParapheZones(circuitId, zones),
    onSuccess: () => qc.invalidateQueries({ queryKey: signatureKeys.all }),
  });
}

export function useSetParapheStamps() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stepId, zoneIds }: { stepId: string; zoneIds: string[] }) =>
      setParapheStamps(stepId, zoneIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: signatureKeys.all }),
  });
}

export function useRejectStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      stepId,
      comment,
      annotations,
    }: { stepId: string; comment: string; annotations?: unknown }) =>
      rejectStep(stepId, comment, annotations ?? []),
    onSuccess: () => qc.invalidateQueries({ queryKey: signatureKeys.all }),
  });
}

export function useCancelCircuit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (circuitId: string) => cancelCircuit(circuitId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: signatureKeys.all });
      qc.invalidateQueries({ queryKey: ['dossier-files'] });
      qc.invalidateQueries({ queryKey: ['dossier-files-all'] });
    },
  });
}

export function useDispatchCircuit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (circuitId: string) => dispatchCircuit(circuitId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: signatureKeys.all });
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useResubmitCircuit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (circuitId: string) => resubmitCircuit(circuitId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: signatureKeys.all });
      qc.invalidateQueries({ queryKey: ['dossier-files'] });
      qc.invalidateQueries({ queryKey: ['dossier-files-all'] });
    },
  });
}

export function useUploadMySignature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadMySignature(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: signatureKeys.mySignatureUrl() }),
  });
}

export function useUploadMyParaphe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadMyParaphe(file),
    onSuccess: (path) => {
      const { collaborator, setCollaborator } = useAuthStore.getState();
      if (collaborator) {
        setCollaborator({ ...collaborator, paraphe_image_path: path } as typeof collaborator);
      }
      qc.invalidateQueries({ queryKey: signatureKeys.myParapheUrl() });
    },
  });
}
